import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { Calendar, ArrowRight } from "lucide-react";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  featured_image_url: string | null;
  publish_date: string | null;
  seo_title: string | null;
  seo_description: string | null;
}

interface Tag {
  id: string;
  name: string;
  slug: string;
}

const Blog = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
    fetchTags();
  }, [selectedTag]);

  const fetchPosts = async () => {
    setIsLoading(true);
    let query = supabase
      .from("blog_posts")
      .select("*")
      .eq("is_published", true)
      .order("publish_date", { ascending: false });

    const { data } = await query;
    
    if (selectedTag && data) {
      // Filter by tag
      const { data: taggedPosts } = await supabase
        .from("blog_post_tags")
        .select("blog_post_id")
        .eq("tag_id", selectedTag);
      
      const taggedIds = taggedPosts?.map(t => t.blog_post_id) || [];
      setPosts(data.filter(p => taggedIds.includes(p.id)));
    } else {
      setPosts(data || []);
    }
    setIsLoading(false);
  };

  const fetchTags = async () => {
    const { data } = await supabase.from("tags").select("*").order("name");
    setTags(data || []);
  };

  return (
    <>
      <Helmet>
        <title>Wedding Blog | Photography Tips & Inspiration | Pixie Clicks</title>
        <meta
          name="description"
          content="Wedding photography tips, inspiration & real love stories. Expert advice for planning your perfect wedding in NJ, FL & CO."
        />
        <meta property="og:title" content="Wedding Blog | Photography Tips & Inspiration | Pixie Clicks" />
        <meta property="og:description" content="Wedding photography tips, inspiration, and real love stories from Pixie Clicks." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pixieclicks.com/blog" />
        <link rel="canonical" href="https://pixieclicks.com/blog" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "Pixie Clicks Wedding Blog",
            "description": "Wedding photography tips, inspiration, and real love stories.",
            "publisher": {
              "@type": "LocalBusiness",
              "name": "Pixie Clicks",
              "areaServed": ["New Jersey", "Florida", "Colorado"]
            }
          })}
        </script>
      </Helmet>
      <Layout>
        {/* Hero */}
        <section className="py-20 md:py-28 bg-muted/30">
          <div className="container-wide text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="font-serif text-4xl font-semibold text-foreground md:text-5xl"
            >
              Wedding <span className="text-primary">Inspiration</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground"
            >
              Tips, stories, and inspiration for your wedding day
            </motion.p>
          </div>
        </section>

        {/* Filter Tags */}
        <section className="py-8 border-b border-border">
          <div className="container-wide">
            <div className="flex flex-wrap gap-2 justify-center">
              <Badge
                variant={selectedTag === null ? "default" : "outline"}
                className="cursor-pointer px-4 py-2"
                onClick={() => setSelectedTag(null)}
              >
                All
              </Badge>
              {tags.map((tag) => (
                <Badge
                  key={tag.id}
                  variant={selectedTag === tag.id ? "default" : "outline"}
                  className="cursor-pointer px-4 py-2"
                  onClick={() => setSelectedTag(tag.id)}
                >
                  {tag.name}
                </Badge>
              ))}
            </div>
          </div>
        </section>

        {/* Posts Grid */}
        <section className="section-padding">
          <div className="container-wide">
            {isLoading ? (
              <div className="flex justify-center py-20">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-20 text-muted-foreground">
                No blog posts yet. Check back soon!
              </div>
            ) : (
              <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                {posts.map((post, index) => (
                  <motion.article
                    key={post.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="group"
                  >
                    <Link to={`/blog/${post.slug}`} className="block">
                      <div className="aspect-[4/3] overflow-hidden rounded-xl bg-muted">
                        {post.featured_image_url ? (
                          <img
                            src={post.featured_image_url}
                            alt={post.title}
                            loading="lazy"
                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        ) : (
                          <div className="flex h-full items-center justify-center bg-muted">
                            <span className="text-muted-foreground">No image</span>
                          </div>
                        )}
                      </div>
                      <div className="mt-4">
                        {post.publish_date && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                            <Calendar className="h-4 w-4" />
                            {format(new Date(post.publish_date), "MMMM d, yyyy")}
                          </div>
                        )}
                        <h2 className="font-serif text-xl font-semibold text-foreground group-hover:text-primary transition-colors">
                          {post.title}
                        </h2>
                        {post.excerpt && (
                          <p className="mt-2 text-muted-foreground line-clamp-2">
                            {post.excerpt}
                          </p>
                        )}
                        <div className="mt-4 flex items-center gap-2 text-primary font-medium">
                          Read More
                          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </div>
                      </div>
                    </Link>
                  </motion.article>
                ))}
              </div>
            )}
          </div>
        </section>
      </Layout>
    </>
  );
};

export default Blog;
